import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Visibility,
  FileCopy,
  Schedule,
} from '@mui/icons-material';
import { SharedContent, WatchlistEntry } from '../types';
import { StockService } from '../services/stockService';
import { SharedContentService } from '../services/sharedContentService';
import { formatDistanceToNow } from 'date-fns';
import { auth } from '../config/firebase';

interface SharedWatchlistViewProps {
  sharedContent: SharedContent;
  onCopyStock?: (symbol: string) => void;
}

const SharedWatchlistView: React.FC<SharedWatchlistViewProps> = ({ 
  sharedContent, 
  onCopyStock 
}) => {
  const [watchlistEntries, setWatchlistEntries] = useState<WatchlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedSymbols, setCopiedSymbols] = useState<Set<string>>(new Set());
  const currentUser = auth.currentUser;

  useEffect(() => {
    loadSharedData();
  }, [sharedContent.userId]);

  const loadSharedData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('[SharedWatchlistView] Step 1: Loading watchlist for userId:', sharedContent.userId);
      console.log('[SharedWatchlistView] Current user:', currentUser?.uid);
      console.log('[SharedWatchlistView] Shared content:', sharedContent);
      
      // Load watchlist entries for the shared user
      const entries = await StockService.getUserWatchlist(sharedContent.userId);
      console.log('[SharedWatchlistView] Step 1 complete: Received entries:', entries.length);
      
      // Filter to only show active entries
      const activeEntries = entries.filter(entry => entry.isActive !== false);
      setWatchlistEntries(activeEntries);
      console.log('[SharedWatchlistView] Step 2: Filtered to', activeEntries.length, 'active entries');
      
      // Try to increment view count (non-blocking)
      console.log('[SharedWatchlistView] Step 3: Incrementing view count for:', sharedContent.id);
      try {
        await SharedContentService.incrementViewCount(sharedContent.id);
        console.log('[SharedWatchlistView] Step 3 complete: View count incremented');
      } catch (viewCountErr: any) {
        console.warn('[SharedWatchlistView] Failed to increment view count:', viewCountErr.message);
        // Continue anyway - don't block viewing just because we can't increment the counter
      }
    } catch (err: any) {
      console.error('[SharedWatchlistView] Full error:', err);
      console.error('[SharedWatchlistView] Error code:', err.code);
      console.error('[SharedWatchlistView] Error message:', err.message);
      console.error('[SharedWatchlistView] Error stack:', err.stack);
      setError(err.message || 'Failed to load shared watchlist');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToWatchlist = async (entry: WatchlistEntry) => {
    if (!currentUser || copiedSymbols.has(entry.symbol)) return;
    
    try {
      // First check if stock already exists in user's watchlist (active or inactive)
      const existsIncludingInactive = await StockService.stockExistsIncludingInactive(entry.symbol);
      if (existsIncludingInactive) {
        // Get the existing entry to check its status
        const userWatchlist = await StockService.getWatchlist(true); // Include inactive
        const existingEntry = userWatchlist.find(e => e.symbol === entry.symbol);
        
        if (existingEntry) {
          if (existingEntry.isActive === false) {
            // Reactivate the stock instead of creating duplicate
            await StockService.reactivateStock(entry.symbol);
            setCopiedSymbols(prev => new Set(prev).add(entry.symbol));
            console.log(`Reactivated existing stock: ${entry.symbol}`);
            alert(`${entry.symbol} has been reactivated in your watchlist`);
          } else {
            // Stock is already active in watchlist
            console.log(`Stock already exists in watchlist: ${entry.symbol}`);
            alert(`${entry.symbol} is already in your watchlist`);
          }
          return;
        }
      }
      
      // Add to current user's watchlist with ALL available fields
      const stockData: any = {
        symbol: entry.symbol,
        companyName: entry.companyName || entry.symbol,
        currentPrice: entry.currentPrice,
        addedToWatchlist: new Date(),
        priceWhenAdded: entry.currentPrice, // Set price when added to current price
        priceChgPercent: 0, // New stocks start at 0% change
        volumeChgVs50Day: 0, // Initialize volume change to 0
      };
      
      // Copy all optional fields if they exist
      if (entry.pivotPrice !== undefined) stockData.pivotPrice = entry.pivotPrice;
      if (entry.targetPrice !== undefined) stockData.targetPrice = entry.targetPrice;
      if (entry.marketSurgeScore !== undefined) stockData.marketSurgeScore = entry.marketSurgeScore;
      if (entry.epsRating !== undefined) stockData.epsRating = entry.epsRating;
      if (entry.rsRating !== undefined) stockData.rsRating = entry.rsRating;
      if (entry.weeklyChgToPivot !== undefined) stockData.weeklyChgToPivot = entry.weeklyChgToPivot;
      if (entry.weeklyBaseType !== undefined && entry.weeklyBaseType !== null) stockData.weeklyBaseType = entry.weeklyBaseType;
      if (entry.weeklyBaseStage !== undefined && entry.weeklyBaseStage !== null) stockData.weeklyBaseStage = entry.weeklyBaseStage;
      if (entry.percentOffHigh !== undefined) stockData.percentOffHigh = entry.percentOffHigh;
      if (entry.volume !== undefined) stockData.volume = entry.volume;
      if (entry.volumeChgVs50Day !== undefined && entry.volumeChgVs50Day !== null) {
        stockData.volumeChgVs50Day = entry.volumeChgVs50Day;
      } else {
        // Default to 0 if not provided
        stockData.volumeChgVs50Day = 0;
      }
      if (entry.marketCap !== undefined) stockData.marketCap = entry.marketCap;
      if (entry.compRating !== undefined) stockData.compRating = entry.compRating;
      if (entry.adRating !== undefined) stockData.adRating = entry.adRating;
      if (entry.smrRating !== undefined) stockData.smrRating = entry.smrRating;
      if (entry.indGroupRank !== undefined) stockData.indGroupRank = entry.indGroupRank;
      if (entry.industryName !== undefined) stockData.industryName = entry.industryName;
      if (entry.avgDailyVolume !== undefined) stockData.avgDailyVolume = entry.avgDailyVolume;
      if (entry.volumeSpikeRatio !== undefined) stockData.volumeSpikeRatio = entry.volumeSpikeRatio;
      if (entry.tradingDayProgress !== undefined) stockData.tradingDayProgress = entry.tradingDayProgress;
      
      await StockService.addToWatchlist(stockData);
      
      // Update copied symbols
      setCopiedSymbols(prev => new Set(prev).add(entry.symbol));
      
      // Increment copy count
      await SharedContentService.incrementCopyCount(sharedContent.id);
      
      if (onCopyStock) {
        onCopyStock(entry.symbol);
      }
    } catch (err: any) {
      console.error('Error copying stock:', err);
      alert(`Failed to copy ${entry.symbol}: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const isOwnWatchlist = currentUser?.uid === sharedContent.userId;

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header with user info */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar
          src={sharedContent.ownerProfilePic}
          sx={{ width: 48, height: 48, mr: 2 }}
        >
          {sharedContent.ownerDisplayName.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">
            {sharedContent.ownerDisplayName}'s Watchlist
          </Typography>
          {sharedContent.ownerHandle && (
            <Typography variant="body2" color="text.secondary">
              @{sharedContent.ownerHandle}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<Visibility />}
            label={`${sharedContent.viewCount} views`}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<FileCopy />}
            label={`${sharedContent.copyCount} copies`}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<Schedule />}
            label={formatDistanceToNow(sharedContent.sharedAt, { addSuffix: true })}
            size="small"
          />
        </Box>
      </Box>

      {/* Watchlist Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Symbol</TableCell>
              <TableCell align="right">Current Price</TableCell>
              <TableCell align="right">Change %</TableCell>
              <TableCell align="right">Pivot Price</TableCell>
              <TableCell align="right">Distance from Pivot</TableCell>
              <TableCell align="right">Target Price</TableCell>
              {!isOwnWatchlist && <TableCell align="center">Action</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {watchlistEntries.map((entry) => (
              <TableRow key={entry.symbol}>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {entry.symbol}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.companyName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  ${entry.currentPrice.toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    {entry.priceChgPercent !== undefined && (
                      <>
                        {entry.priceChgPercent >= 0 ? (
                          <TrendingUp color="success" fontSize="small" />
                        ) : (
                          <TrendingDown color="error" fontSize="small" />
                        )}
                        <Typography
                          variant="body2"
                          color={entry.priceChgPercent >= 0 ? 'success.main' : 'error.main'}
                          sx={{ ml: 0.5 }}
                        >
                          {entry.priceChgPercent.toFixed(2)}%
                        </Typography>
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {entry.pivotPrice ? `$${entry.pivotPrice.toFixed(2)}` : '-'}
                </TableCell>
                <TableCell align="right">
                  {entry.distanceFromPivot !== null ? (
                    <Chip
                      label={`${entry.distanceFromPivot.toFixed(1)}%`}
                      size="small"
                      color={
                        Math.abs(entry.distanceFromPivot) <= 2 ? 'success' :
                        entry.distanceFromPivot > 0 ? 'primary' : 'default'
                      }
                    />
                  ) : '-'}
                </TableCell>
                <TableCell align="right">
                  {entry.targetPrice ? `$${entry.targetPrice.toFixed(2)}` : '-'}
                </TableCell>
                {!isOwnWatchlist && (
                  <TableCell align="center">
                    <Tooltip title={copiedSymbols.has(entry.symbol) ? "Already copied" : "Copy to my watchlist"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyToWatchlist(entry)}
                          disabled={copiedSymbols.has(entry.symbol)}
                          color={copiedSymbols.has(entry.symbol) ? "success" : "primary"}
                        >
                          <FileCopy fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {watchlistEntries.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            No stocks in this watchlist
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default SharedWatchlistView;